<template>
  <StandardWrapper
    v-bind="$attrs"
    :blok="blok"
    :class="{ hidden: !showBlock }"
    :allow-overflow="true"
  >
    <h2
      v-if="blok.title"
      class="title"
      :class="`sm:text-center ${
        cardStyle === 'raised-2' ? 'text-3xl font-medium' : ''
      } ${textColorClass(blok.titleVariant, blok.bgVariant, 'text-darker')}`"
    >
      {{ blok.title }}
    </h2>

    <h3
      v-if="blok.subtitle"
      class="subtitle mt-4 lg:mt-10"
      :class="
        textColorClass(blok.subtitleVariant, blok.bgVariant, 'text-darker')
      "
    >
      {{ blok.subtitle }}
    </h3>

    <RichText
      :blok="blok"
      :class="[{ 'mt-6 lg:mt-8': blok.title || blok.subtitle }]"
    />

    <div ref="startOfList" />

    <SearchPanel
      v-if="blok.showSearchBar || blok.showSearchFilters"
      v-model="searchQuery"
      :blok="blok"
      :filters="filters"
      search-placeholder="Functie, trefwoord of bedrijfsnaam"
      search-aria-label="Zoek vacatures"
      class="mt-6 md:mt-12"
    />

    <div
      v-if="blok.enableSorting"
      :id="`sorting${blok._uid}`"
      class="mt-8 mb-4 flex flex-col justify-between md:flex-row md:items-center"
      :class="textColorClass(blok.textVariant, blok.bgVariant, 'text-darker')"
    >
      <p>
        {{ resultsHeader }}
        <span
          class="ml-1"
          :class="
            isBackgroundDark(blok.bgVariant) ? 'text-gray-100' : 'text-gray-600'
          "
          >({{ page.totalElements }})</span
        >
      </p>

      <div
        class="mt-2 flex w-full justify-between gap-2 text-xs md:mt-0 md:w-auto md:items-center md:gap-6"
      >
        <div class="flex w-full flex-col md:w-auto md:flex-row md:items-center">
          <label :for="`dateFilter${blok._uid}`">Plaatsingsdatum</label>
          <select
            :id="`dateFilter${blok._uid}`"
            v-model="dateFilter"
            name="dateFilter"
            class="block w-full rounded-md bg-white py-1 pr-8 pl-3 text-xs text-gray-900 shadow-xs focus:outline-2 md:ml-2"
          >
            <option value="" selected>toon alles</option>
            <option value="1">afgelopen 24 uur</option>
            <option value="3">afgelopen 3 dagen</option>
            <option value="7">afgelopen 7 dagen</option>
          </select>
        </div>

        <div class="flex w-full flex-col md:w-auto md:flex-row md:items-center">
          <label :for="`sorting${blok._uid}`">Sorteer</label>
          <select
            :id="`sorting${blok._uid}`"
            v-model="sorting"
            name="sort"
            class="block w-full rounded-md bg-white py-1 pr-8 pl-3 text-xs text-gray-900 shadow-xs focus:outline-2 md:ml-2"
          >
            <option value="default" selected="">relevantie</option>
            <option value="startDate">plaatsingsdatum</option>
          </select>
        </div>
      </div>
    </div>

    <JobAlert
      v-if="blok.jobAlert?.length === 1"
      :class="{ '-mb-6': vacanciesToDisplay.length && !blok.noTextPadding }"
      :blok="blok"
      :context="context"
      :item="blok.jobAlert[0]"
      :search-query="searchQuery"
      :filters="filters"
    />

    <div :class="{ 'my-6': !vacancies.length }">
      <RichText
        :blok="{ ...blok, textContent: null }"
        class="no-vacancies-message"
        :rich-text-content="
          searchActive ? noSearchResultsMessage : noVacanciesMessage
        "
      />
    </div>

    <div v-if="loading">
      <LoadingState />
    </div>

    <div v-else-if="vacancies.length" class="vacancies-container mx-auto">
      <ul
        class="grid grid-cols-1 items-stretch gap-x-4 gap-y-6"
        :class="[gridClass, blok.noTextPadding ? 'mt-2' : 'mt-12']"
      >
        <li v-for="vacancyCard in vacanciesToDisplay" :key="vacancyCard.id">
          <VacancyCard :vacancy="vacancyCard" :blok="blok" :context="context" />
        </li>
      </ul>
    </div>

    <div v-show="!loading">
      <Pagination
        v-if="blok.enablePagination"
        v-model="page"
        page-size-label="vacatures per pagina"
      />

      <div
        v-else-if="showBottomLink"
        class="mt-6 flex justify-center"
        :class="linkClass"
      >
        <a
          class="see-more-link text-lg hover:underline focus:underline"
          :href="bottomLinkUrl"
          @click="handleBottomLinkClick"
          >{{ bottomLinkLabel }}</a
        >
      </div>
    </div>
  </StandardWrapper>
</template>

<script setup>
import {
  computed,
  onServerPrefetch,
  onBeforeMount,
  ref,
  watch,
  nextTick,
} from 'vue'
import StandardWrapper from '../components/StandardWrapper.vue'
import RichText from '../components/RichText.vue'
import LoadingState from '../components/LoadingState.vue'
import Pagination from '../components/Pagination.vue'
import { fetchTagList, fetchVacancyList } from '../api/tooContentDelivery.js'
import { useColorPalette } from '../composables/colorPalette'
import { useHead } from '@unhead/vue'
import { usePageContext } from '../renderer/usePageContext'
import { renderRichText } from '@storyblok/vue'
import JobAlert from '../components/JobAlert.vue'
import SearchPanel from '../components/SearchPanel.vue'
import VacancyCard from '../components/VacancyCard.vue'
import { useMainStore } from '../store/index.js'
import { storeToRefs } from 'pinia'
import { usePagination } from '../composables/pagination.js'
import { useSearch } from '../composables/search.js'

const props = defineProps({
  blok: {
    type: Object,
    default: () => {},
  },
  context: {
    type: Object,
    default: () => {},
  },
})

const store = useMainStore()
const { userToken } = storeToRefs(store)

const { textColorClass, colorCustomClass, isBackgroundDark, contrastingGrey } =
  useColorPalette()

const vacancies = ref([])
const loading = ref(false)
const filters = ref({
  employmentType: [],
  discipline: [],
  education: [],
  workCategory: [],
  workExperience: [],
  region: [],
})

let companyId = props.context.company?.id
if (props.context.vacancy || props.blok.includeAllSiteVacancies) {
  companyId = undefined
}
const companyIds = companyId ? [companyId] : []

const { searchQuery } = useSearch(props.blok, props.context, companyIds)
const { page } = usePagination()

const sorting = ref('default')
const dateFilter = ref('')
const showRemainingVacancies = ref(false)

const startOfList = ref(null)

const cardStyle = computed(() => {
  return props.blok.style || 'raised'
})
const vacanciesToDisplay = computed(() => {
  if (!showRemainingVacancies.value && props.blok.maxVacancies > 0) {
    return vacancies.value.slice(0, props.blok.maxVacancies)
  }
  return vacancies.value
})
const searchActive = computed(() => {
  if (props.blok.showSearchBar) {
    if (searchQuery.value.filter) {
      return true
    }

    if (
      props.blok.includeLocationInSearch &&
      searchQuery.value.geoLocation.latitude
    ) {
      return true
    }
  }

  if (props.blok.showSearchFilters) {
    const searchTags = [
      ...searchQuery.value.employmentType,
      ...searchQuery.value.discipline,
      ...searchQuery.value.education,
      ...searchQuery.value.workCategory,
      ...searchQuery.value.workExperience,
      ...searchQuery.value.region,
    ]
    if (searchTags.length) {
      return true
    }
  }

  if (props.blok.enableSorting && dateFilter.value) {
    return true
  }

  return false
})
const showBlock = computed(() => {
  return (
    loading.value ||
    props.blok.showNoVacanciesMessage ||
    vacancies.value.length ||
    props.blok.showSearchBar ||
    props.blok.showSearchFilters
  )
})
const noVacanciesMessage = computed(() => {
  if (
    props.blok.showNoVacanciesMessage &&
    !vacancies.value.length &&
    !loading.value
  ) {
    return props.blok.noVacanciesMessage
  }
  return false
})
const noSearchResultsMessage = computed(() => {
  if (!vacancies.value.length && !loading.value) {
    const renderedText = renderRichText(props.blok.noSearchResultsMessage || '')
    if (!renderedText || renderedText === '<p></p>') {
      return 'Er zijn geen vacatures die aan de zoekresultaten voldoen'
    }
    return props.blok.noSearchResultsMessage
  }
  return false
})
const resultsHeader = computed(() => {
  let header = searchActive.value ? 'Vacatures' : 'Alle vacatures'
  if (searchQuery.value.filter) {
    header += ` voor "${searchQuery.value.filter}"`
  }
  if (searchQuery.value.geoLocation.label) {
    header += ` in ${searchQuery.value.geoLocation.label}`
  }
  return header
})
const highlightVariable = computed(() => {
  return `var(--${colorCustomClass(props.blok.searchColor || 'default', null, '')})`
})
const gridClass = computed(() => {
  if (!props.blok.cols || vacanciesToDisplay.value.length < 2) {
    return 'grid-cols-none'
  }
  // To make sure dynamically defined css classes are not purged, it's enough to add them as comment
  return `md:grid-cols-${Math.min(props.blok.cols, 2)} lg:grid-cols-${props.blok.cols}`
  // md:grid-cols-1 md:grid-cols-2
  // lg:grid-cols-1 lg:grid-cols-2 lg:grid-cols-3 lg:grid-cols-4 lg:grid-cols-5 lg:grid-cols-6
})

const showBottomLink = computed(() => {
  if (loading.value) return false

  if (props.blok.bottomLinkCategory === 'truncated-list') {
    return vacanciesToDisplay.value.length < vacancies.value.length
  }
  if (props.blok.bottomLinkCategory === 'region') {
    return !!props.context.region
  }
  if (props.blok.bottomLinkCategory === 'discipline') {
    return props.context.vacancy?.discipline?.length > 0
  }

  return false
})
const bottomLinkUrl = computed(() => {
  if (props.blok.bottomLinkCategory === 'region' && props.context.region) {
    return `${props.blok.bottomLinkPrefix}/${props.context.region}`
  }
  if (props.blok.bottomLinkCategory === 'discipline') {
    const disciplines = props.context.vacancy?.discipline
    if (disciplines?.length) {
      return `${props.blok.bottomLinkPrefix}/${disciplines[0].key}`
    }
  }
  return ''
})
const bottomLinkLabel = computed(() => {
  if (props.blok.bottomLinkCategory === 'region' && props.context.region) {
    // We have the region slug, which is something like: noord-holland
    const regionLabel = props.context.region
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('-')
    return props.blok.bottomLinkLabel || `Werken in ${regionLabel} >`
  }
  return props.blok.bottomLinkLabel || ''
})
const linkClass = computed(() => {
  if (props.blok.linkTextColor) {
    return `richtext-link ${props.blok.linkTextColor}`
  }

  return textColorClass(
    props.blok.textVariant,
    props.blok.bgVariant || 'white',
    'default',
    'white',
  )
})

onServerPrefetch(async () => {
  await fetchVacancies()
  if (props.blok.showSearchFilters) {
    await fetchSearchFilters()
  }
  store.prefetchedData[props.blok._uid] = {
    vacancies: vacancies.value,
    page: page.value,
    filters: filters.value,
  }
})

onBeforeMount(() => {
  if (store.prefetchedData[props.blok._uid]) {
    vacancies.value = store.prefetchedData[props.blok._uid].vacancies
    page.value = store.prefetchedData[props.blok._uid].page
    filters.value = store.prefetchedData[props.blok._uid].filters
  } else {
    fetchVacancies()
    if (props.blok.showSearchFilters) {
      fetchSearchFilters()
    }
  }
})

async function fetchSearchFilters() {
  const tagGroups = await fetchTagList({
    domainSlug: props.context.domainSlug,
    baseTooUrl: import.meta.env.VITE_BASE_TOO_API_URL,
  })

  tagGroups?.forEach((group) => {
    let category
    switch (group.tagCategory) {
      case 'VACANCY_EDUCATION':
        category = 'education'
        break
      case 'VACANCY_EMPLOYMENTTYPE':
        category = 'employmentType'
        break
      case 'VACANCY_WORKCATEGORY':
        category = 'workCategory'
        break
      case 'VACANCY_WORKEXPERIENCE':
        category = 'workExperience'
        break
      case 'VACANCY_WORKFIELD':
        category = 'discipline'
        break
      case 'REGION':
        category = 'region'
        break
    }

    if (group) {
      filters.value[category].push(...group.tags)
    }
  })
}

async function fetchVacancies() {
  if (loading.value) {
    return
  }

  loading.value = true
  vacancies.value = []
  page.value.totalElements = 0
  page.value.totalPages = 0

  try {
    // Backend doesn't want us to send location distance if there's no location selected
    let geoLocation = searchQuery.value.geoLocation
    if (!geoLocation.latitude) {
      geoLocation = undefined
    }

    const { data, meta } = await fetchVacancyList({
      searchParams: {
        ...searchQuery.value,
        geoLocation,
        publishedSinceDays: dateFilter.value,
      },
      page: {
        page: page.value.number,
        size: props.blok.enablePagination ? page.value.size : 999,
      },
      sortBy: sorting.value,
      jwt: userToken.value,
      domainSlug: props.context.domainSlug,
      baseTooUrl: import.meta.env.VITE_BASE_TOO_API_URL,
    })

    vacancies.value = data
    if (props.context.vacancy) {
      vacancies.value = vacancies.value.filter(
        (vacancy) => vacancy.id !== props.context.vacancy.id,
      )
    }

    page.value.totalElements = meta.page.totalElements
    page.value.totalPages = meta.page.totalPages

    // We requested an unexisting page, we should fetch the vacancies again
    if (page.value.totalPages && page.value.totalPages < page.value.number) {
      page.value.number = page.value.totalPages
    }
  } catch (error) {
    console.error(error)
  } finally {
    // TODO We used to debounce a little the loading indicator so it doesn't display when
    // the changes are immediate, eg: a vacancy was set as favorite
    loading.value = false
  }
}

function handleBottomLinkClick(event) {
  if (props.blok.bottomLinkCategory === 'truncated-list') {
    showRemainingVacancies.value = true
    event.preventDefault()
  }
}

watch(
  userToken,
  () => {
    fetchVacancies()
  },
  { once: true },
)
watch([sorting, dateFilter], () => {
  page.value.number = 1
  fetchVacancies()
})
watch(
  searchQuery,
  () => {
    page.value.number = 1
    fetchVacancies()
  },
  { deep: true },
)
watch(
  () => page.value.number,
  async () => {
    fetchVacancies()
    await nextTick()
    startOfList.value?.scrollIntoView()
  },
)
watch(
  () => page.value.size,
  async () => {
    fetchVacancies()
    await nextTick()
    startOfList.value?.scrollIntoView()
  },
)

const pageContext = usePageContext()
useHead(
  () => {
    // We need to set CSS vars inside <head><style> instead of using v-bind() directly inside <style scoped>
    // because v-bind generates style="..." and that's not allowed with CSP enabled.
    const inputBorderColor = contrastingGrey(props.blok.bgVariant)
    const style = [
      {
        key: props.blok._uid,
        nonce: pageContext.nonce,
        innerHTML: `
          #sorting${props.blok._uid} {
            --btn-color: var(--${colorCustomClass(props.blok.searchColor || 'default', null, '')});
            border-color: ${inputBorderColor};
          }
      `,
      },
    ]

    const meta = []
    if (searchActive.value && !props.context.searchParams?.tagCategory) {
      // We don't want to index pages with search filters active
      meta.push({
        name: 'robots',
        content: 'noindex',
      })
    }

    return {
      style,
      meta,
    }
  },
  { mode: import.meta.env.VITE_IS_EDITOR ? 'client' : 'server' },
)
</script>

<style scoped>
input:focus,
select:focus {
  border-color: var(--btn-color);
  outline-color: var(--btn-color);
}

li.active {
  background-color: color-mix(in oklab, v-bind(highlightVariable), white 95%);
  color: color-mix(in oklab, v-bind(highlightVariable), black 30%);
}
</style>
